import React, { useState, useEffect, useRef } from 'react';
import {
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  MinusCircleFilled,
  NodeCollapseOutlined,
  PlusCircleFilled,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import {
  GroupNodeContent,
  GroupNodeHeader,
  GroupNodeHeaderIcon,
  GroupNodeHeaderLeft,
  GroupNodeHeaderName,
  GroupNodeWrapper,
  DeviceNodeIcon,
  DeviceNodeName,
  DeviceNodeWrapper,
  DeviceNodeActions,
  DeviceNodeAction,
  GroupNodeHeaderActions,
  GroupNodeHeaderAction,
} from '../../Composable';
import CameraOnIcon from '../../../../assets/icons/camera_on.svg';
import CameraOffIcon from '../../../../assets/icons/camera_off.svg';
import { useLatestCameraState } from '../../../../hooks/use-latest-camera-state';

import './styles.scss';
import { Spin, Tooltip } from 'antd';
import { ROOT_GROUP_ID } from '../../../../constants/group';

export const RootTree = ({
  onSelect,
  selectedId,
  rootGroups,
  getChildren,
  getCameras,
  groupMap,
  onSelectCamera,
  getIsExpanded,
  onExpand,
  getShouldScrollAfterLoad,
  getIsCameraSelected,
  getIsCameraPinned,
  onPinCamera,
  filterChildren,
  showCollapseAll,
  onCollapseAll,
  showVisibilityToggle,
  onShowVisibility,
  isShowAll,
}) => {
  const onNodeSelect = (groupId) => {
    onSelect(groupMap.current[groupId]);
  };

  const isExpanded = true;
  const isSelected = selectedId === ROOT_GROUP_ID;
  const level = 0;

  return (
    <div id="live-root-tree">
      <GroupNodeWrapper
        level={level}
        isExpanded={isExpanded}
        id={`live-group-${ROOT_GROUP_ID}`}
        isSelected={isSelected}
      >
        <GroupNodeHeader>
          <GroupNodeHeaderLeft
            onClick={() => onSelect({ id: ROOT_GROUP_ID, name: 'All groups' })}
          >
            <GroupNodeHeaderIcon>
              <MinusCircleFilled />
            </GroupNodeHeaderIcon>

            <GroupNodeHeaderName>All groups</GroupNodeHeaderName>
          </GroupNodeHeaderLeft>

          <GroupNodeHeaderActions>
            {showVisibilityToggle && (
              <Tooltip
                title={isShowAll ? 'Hide others groups' : 'Show all groups'}
              >
                <GroupNodeHeaderAction onClick={onShowVisibility}>
                  {isShowAll ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </GroupNodeHeaderAction>
              </Tooltip>
            )}
            {showCollapseAll && (
              <Tooltip title="Collapse all but the selected group">
                <GroupNodeHeaderAction onClick={onCollapseAll}>
                  <NodeCollapseOutlined />
                </GroupNodeHeaderAction>
              </Tooltip>
            )}
          </GroupNodeHeaderActions>
        </GroupNodeHeader>
        <GroupNodeContent
          style={{ maxHeight: 'calc(100dvh - 248px)', overflowY: 'auto' }}
        >
          {rootGroups
            ?.filter((c) => filterChildren(c, level + 1))
            ?.map((group) => (
              <GroupNode
                key={group.id}
                id={group.id}
                name={group.name}
                getChildren={getChildren}
                getIsSelected={(id) => id === selectedId}
                isParentSelected={false}
                getIsExpanded={getIsExpanded}
                onSelect={onNodeSelect}
                onExpand={onExpand}
                level={1}
                getCameras={getCameras}
                onSelectCamera={onSelectCamera}
                getShouldScrollAfterLoad={getShouldScrollAfterLoad}
                getIsCameraSelected={getIsCameraSelected}
                getIsCameraPinned={getIsCameraPinned}
                onPinCamera={onPinCamera}
                filterChildren={filterChildren}
              />
            ))}
        </GroupNodeContent>
      </GroupNodeWrapper>
    </div>
  );
};

export const GroupNode = ({
  name,
  id,
  getChildren,
  getIsSelected,
  onSelect,
  getIsExpanded,
  onExpand,
  level,
  getCameras,
  getCameraTooltip,
  getTooltip,
  onSelectCamera = () => {},
  getIsCameraSelected = () => false,
  getShouldScrollAfterLoad,
  getIsCameraPinned,
  onPinCamera,
  filterChildren = () => true,
}) => {
  const [cameras, setCameras] = useState([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [children, setChildren] = useState(undefined);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const isExpanded = getIsExpanded(id);
  const isSelected = getIsSelected(id);

  const isLoading = isLoadingChildren || isLoadingCameras;

  const ref = useRef(null);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    setIsLoadingChildren(true);
    getChildren(id)
      .then(setChildren)
      .finally(() => setIsLoadingChildren(false));
    setIsLoadingCameras(true);
    getCameras(id)
      .then(setCameras)
      .finally(() => setIsLoadingCameras(false));
  }, [id, getChildren, isExpanded, getCameras]);

  useEffect(() => {
    if (getShouldScrollAfterLoad?.(id)) {
      console.log('scroll to', id, ref.current);
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    getShouldScrollAfterLoad,
    isExpanded,
    children !== undefined,
    cameras !== undefined,
    isLoadingChildren,
    isLoadingCameras,
    id,
  ]);

  const renderChildren = () => {
    if (!isExpanded) {
      return null;
    }

    return (
      <GroupNodeContent>
        {children
          ?.filter((c) => filterChildren(c, level + 1))
          ?.map((child) => (
            <GroupNode
              key={child.id}
              name={child.name}
              id={child.id}
              getChildren={getChildren}
              getIsSelected={getIsSelected}
              getIsExpanded={getIsExpanded}
              onSelect={onSelect}
              onExpand={onExpand}
              level={level + 1}
              getCameras={getCameras}
              getTooltip={getTooltip}
              getCameraTooltip={getCameraTooltip}
              onSelectCamera={onSelectCamera}
              getIsCameraSelected={getIsCameraSelected}
              getShouldScrollAfterLoad={getShouldScrollAfterLoad}
              getIsCameraPinned={getIsCameraPinned}
              onPinCamera={onPinCamera}
              filterChildren={filterChildren}
            />
          ))}

        <div
          className="group-node-cameras-container"
          style={{ maxHeight: 36 * 10, overflowY: 'auto' }}
        >
          {cameras?.map((cam) => (
            <WithLatestStateDeviceNode
              key={cam.serial}
              level={level + 1}
              state={cam.state}
              name={cam.name}
              tooltip={getCameraTooltip ? getCameraTooltip(cam) : cam.name}
              onSelect={() => onSelectCamera(cam, id)}
              isSelected={getIsCameraSelected(cam.serial)}
              serial={cam.serial}
              allowRemove={false}
              onRemove={() => {}}
              onPin={(pinned) => onPinCamera(cam, pinned)}
              isPinned={getIsCameraPinned?.(cam.serial) || false}
            />
          ))}
        </div>
      </GroupNodeContent>
    );
  };

  return (
    <GroupNodeWrapper
      level={level}
      isExpanded={isExpanded}
      isSelected={isSelected}
      id={`live-group-${id}`}
      ref={ref}
    >
      <GroupNodeHeader>
        <GroupNodeHeaderLeft>
          <GroupNodeHeaderIcon
            onClick={() => {
              onExpand(id);
            }}
          >
            {isExpanded ? <MinusCircleFilled /> : <PlusCircleFilled />}
          </GroupNodeHeaderIcon>

          <GroupNodeHeaderName
            tooltip={getTooltip ? getTooltip({ name, id }) : undefined}
            onClick={() => onSelect(id)}
          >
            {name}
          </GroupNodeHeaderName>
        </GroupNodeHeaderLeft>
        {isLoading && (
          <GroupNodeHeaderActions>
            <GroupNodeHeaderAction>
              <Spin size="small" indicator={<LoadingOutlined spin />} />
            </GroupNodeHeaderAction>
          </GroupNodeHeaderActions>
        )}
      </GroupNodeHeader>
      {renderChildren()}
    </GroupNodeWrapper>
  );
};

export const DeviceNode = ({
  level,
  state,
  name,
  tooltip,
  onSelect,
  isSelected,
  allowRemove,
  onRemove,
  removeTooltip,
  onPin,
  isPinned,
}) => {
  return (
    <DeviceNodeWrapper
      level={level}
      isInactive={(state || 0) === 0}
      tooltip={tooltip}
      isSelected={isSelected}
    >
      <DeviceNodeIcon>
        <img src={state !== 1 ? CameraOffIcon : CameraOnIcon} />
      </DeviceNodeIcon>
      <DeviceNodeName onClick={() => onSelect?.()}>{name}</DeviceNodeName>
      <DeviceNodeActions>
        <DeviceNodeAction onClick={() => onPin?.(!isPinned)}>
          {isPinned ? <PushpinFilled /> : <PushpinOutlined />}
        </DeviceNodeAction>
        {allowRemove && (
          <Tooltip title={removeTooltip}>
            <DeviceNodeAction onClick={onRemove}>
              <DeleteOutlined />
            </DeviceNodeAction>
          </Tooltip>
        )}
      </DeviceNodeActions>
    </DeviceNodeWrapper>
  );
};

export const WithLatestStateDeviceNode = ({
  level,
  state,
  name,
  tooltip,
  onSelect,
  isSelected,
  serial,
  allowRemove,
  onRemove,
  removeTooltip,
  onPin,
  isPinned,
}) => {
  const latestState = useLatestCameraState({ serial, defaultState: state });

  return (
    <DeviceNode
      level={level}
      state={latestState}
      name={name}
      tooltip={tooltip}
      onSelect={onSelect}
      isSelected={isSelected}
      allowRemove={allowRemove}
      onRemove={onRemove}
      removeTooltip={removeTooltip}
      onPin={onPin}
      isPinned={isPinned}
    />
  );
};
